import { graphql } from '@/graphql-setup'
import { UserFragments } from '@/modules/users/fragments'
import { gql } from '@apollo/client'

export const USER_TIME_ENTRIES_QUERY = graphql(`
  query UserTimeEntries(
    $user: ID
    $datetimeStartLt: DateTime
    $datetimeStartGt: DateTime
    $datetimeEndLt: DateTime
    $datetimeEndGt: DateTime
    $overlapsWith: [ID!]
  ) {
    allTimeEntries(
      user: $user
      datetimeStart_Lt: $datetimeStartLt
      datetimeStart_Gt: $datetimeStartGt
      datetimeEnd_Lt: $datetimeEndLt
      datetimeEnd_Gt: $datetimeEndGt
      overlapsWith: $overlapsWith
    ) {
      edges {
        node {
          id

          datetimeStart
          datetimeEnd

          comment
          overtimeMode

          totalWork
          pause
          overtime

          user {
            id
            fullName
            userTypes {
              id
            }
          }
        }
      }
    }
  }
`)

export const USER_ABSENCES_QUERY = graphql(`
  query UserAbsences(
    $users: [ID]
    $absenceType: ID
    $requestStatus: String
    $leaderGroup: ID
    $datetimeStartGte: DateTime
    $datetimeStartLte: DateTime
  ) {
    pendingAbsenceCount: absenceCountByStatus(status: PENDING)
    allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: $requestStatus
      leaderGroup: $leaderGroup
      datetimeStart_Gte: $datetimeStartGte
      datetimeStart_Lte: $datetimeStartLte
      orderBy: "-datetimeStart"
    ) {
      total
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus

          user {
            id
            fullName
          }
          absenceType {
            id
            name
            color

            preset
            durationMode
          }
        }
      }
    }
  }
`)

export const ABSENCE_REQUESTS_QUERY = graphql(`
  query AbsenceRequests(
    $users: [ID]
    $absenceType: ID
    $leaderGroup: ID
    $after: String
    $first: Int!
  ) {
    allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: "pending"
      leaderGroup: $leaderGroup
      orderBy: "-datetimeStart"
      after: $after
      first: $first
    ) {
      total
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus

          user {
            id
            fullName
          }
          absenceType {
            id
            name
            color

            preset
            durationMode
          }
          periodReport {
            periodStart
            periodEnd

            hoursMax
            hoursUsed
            hoursRemaining
            absencesTotal
            absencesMax
            absencesRemaining

            absenceType {
              name
              durationMode
              maxHoursContinuous
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const ABSENCE_TYPE_PERIOD_REPORT_QUERY = graphql(`
  query AbsenceTypePeriodReport(
    $userId: ID!
    $absenceTypeId: ID!
    $when: DateTime
  ) {
    absenceTypePeriodReport(
      userId: $userId
      absenceTypeId: $absenceTypeId
      when: $when
    ) {
      absenceType {
        id
        name
        interval
        durationMode
        maxHoursContinuous
      }

      periodStart
      periodEnd

      hoursMax
      hoursUsed
      hoursRemaining

      absencesTotal
      absencesMax
      absencesRemaining
    }
  }
`)

export const ALL_ABSENCES_QUERY = gql`
  query AllAbsences($first: Int!, $orderBy: String) {
    allAbsences(first: $first, orderBy: $orderBy) {
      edges {
        node {
          id
          absenceType {
            id
            name
          }
          datetimeStart
          datetimeEnd
          reason
          user {
            ...UserInfo
          }
        }
      }
    }
  }
  ${UserFragments.info}
`

export const ALL_ABSENCE_TYPES_QUERY = graphql(`
  query AllAbsenceTypes {
    allAbsenceTypes {
      id
      externalId
      defaultDayValue

      name
      color
      preset

      interval
      resetMode
      durationMode

      maxHours
      maxHoursContinuous
      maxAbsencesInInterval
    }
  }
`)

export const ABSENCE_TYPES_COMBOBOX_QUERY = graphql(`
  query AbsenceTypesCombobox {
    allAbsenceTypes {
      id
      name
      color

      preset
      durationMode
    }
    allUsersList {
      id
      childcare {
        numberOfChildren
      }
    }
  }
`)

export const ALL_TIME_ENTRY_PROJECTS_QUERY = gql`
  query AllTimeEntryProjects($first: Int, $orderBy: String, $q: String) {
    allTimeEntryProjects(first: $first, orderBy: $orderBy, q: $q) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

export const TIMESHEETS_REPORT_QUERY = graphql(`
  query TimesheetsReport(
    $userId: ID!
    $startDate: DateTime!
    $endDate: DateTime!
    $includeWholeWeeks: Boolean
  ) {
    timesheetsReport(
      userId: $userId
      startDate: $startDate
      endDate: $endDate
      includeWholeWeeks: $includeWholeWeeks
    ) {
      totalOrdinary
      totalWork
      totalPause
      overtime
      user {
        id
        fullName
        userTypes {
          id
          name
        }
      }
      salaryWorkTime {
        id
        pauseThreshold
        pauseDuration
        salaryDateStart
        salaryDateEnd
        availableDepartments {
          id
          name
        }
        defaultDepartment {
          id
          name
        }
        dailyWorkLimit
        usePeriodOvertime
      }
      periodReports {
        periodStart
        periodEnd

        totalOrdinary
        totalWork
        totalPause

        periodOvertime
        dayOvertime
        overtime

        dayReports {
          totalOrdinary
          totalWork
          totalOvertime
          totalPause

          day
          dayStart
          dayEnd

          comments

          project {
            id
            name
          }
          department {
            id
            name
          }

          timeEntries {
            id
            comment
            datetimeStart
            datetimeEnd

            totalOrdinary
            totalWork
            pause
            overtime
            user {
              id
            }
            diet
            salaryCodeOvertime {
              id
              name
              externalId
            }
            salaryCodeOrdinary {
              id
              name
              externalId
            }
            project {
              id
              name
            }
            department {
              id
              name
            }
          }
          absences {
            id
            datetimeStart
            datetimeEnd
            reason
            requestStatus
            absenceType {
              id
              name
            }
            user {
              id
              fullName
              userTypes {
                id
              }
            }
          }
          overtimeEntries {
            startTime
            endTime
            overtime
            salaryCode {
              id
              name
              externalId
            }
            project {
              id
              name
            }
            department {
              id
              name
            }
          }
        }
      }
    }
  }
`)

export const ABSENCE_QUERY = gql`
  query GetAbsence($id: ID!) {
    absence(id: $id) {
      id
      datetimeStart
      datetimeEnd
      reason
      requestStatus
      user {
        id
        fullName
      }
      absenceType {
        id
        name
      }
    }
  }
`
